const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";
const DEFAULT_TIMEOUT_MS = 60000;
const MAX_RETRIES = 1;
const RETRY_STATUSES = [502, 503, 504];

async function request(path, options = {}, { retries = MAX_RETRIES, timeoutMs = DEFAULT_TIMEOUT_MS } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const fetchOptions = {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
    signal: controller.signal,
  };

  let lastError = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(`${API_BASE_URL}${path}`, fetchOptions);
      clearTimeout(timer);

      const contentType = response.headers.get("content-type") || "";
      const body = contentType.includes("application/json")
        ? await response.json()
        : await response.text();

      if (!response.ok) {
        const message = body?.error || body?.message || (typeof body === "string" && body.length < 200 ? body : null) || `Erreur ${response.status}`;
        if (RETRY_STATUSES.includes(response.status) && attempt < retries) {
          lastError = new Error(message);
          await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
          continue;
        }
        throw new Error(message);
      }

      return body;
    } catch (error) {
      clearTimeout(timer);
      if (error.name === "AbortError") {
        throw new Error("Requête expirée (timeout). Réessayez ou vérifiez la connexion.");
      }
      if (attempt < retries && !error.message?.includes("Erreur")) {
        lastError = error;
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
        continue;
      }
      throw error;
    }
  }
  throw lastError || new Error("Erreur réseau");
}

async function requestRaw(path, options = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      credentials: "include",
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Erreur ${response.status}`);
    }
    return response;
  } catch (error) {
    clearTimeout(timer);
    if (error.name === "AbortError") {
      throw new Error("Requête expirée (timeout).");
    }
    throw error;
  }
}

export const apiClient = {
  request,
  publicCaptureLead: (payload) =>
    request("/api/leads", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  adminLogin: (payload) =>
    request("/api/admin/login", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  adminLogout: () =>
    request("/api/admin/logout", {
      method: "POST",
    }),
  adminMe: () => request("/api/admin/me"),
  adminProspectionSearch: (payload) =>
    request("/api/admin/prospection/search", {
      method: "POST",
      body: JSON.stringify(payload),
    }, { timeoutMs: 180000 }),
  adminProspectionPagesJaunesScrape: (payload) =>
    request("/api/admin/prospection/pagesjaunes-scrape", {
      method: "POST",
      body: JSON.stringify(payload),
    }, { timeoutMs: 300000 }),
  adminProspectsImportSearch: (payload) =>
    request("/api/admin/prospects/import-search", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  adminProspectsImportItems: (payload) =>
    request("/api/admin/prospects/import", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  adminProspectsList: (query = "") => request(`/api/admin/prospects${query ? `?${query}` : ""}`),
  adminProspectsShortlist: (query = "") =>
    request(`/api/admin/prospects/shortlist${query ? `?${query}` : ""}`),
  adminProspectsWhyNot: () => request("/api/admin/prospects/why-not"),
  adminProspectById: (id) => request(`/api/admin/prospects/${id}`),
  adminProspectPatch: (id, payload) =>
    request(`/api/admin/prospects/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  adminProspectRecalculate: (id) =>
    request(`/api/admin/prospects/${id}/recalculate`, {
      method: "POST",
    }),
  adminProspectConvertToLead: (id) =>
    request(`/api/admin/prospects/${id}/convert-to-lead`, {
      method: "POST",
    }),
  adminProspectEnrich: (id, source) =>
    request(`/api/admin/prospects/${id}/enrich`, {
      method: "POST",
      body: JSON.stringify({ source }),
    }, { timeoutMs: 30000 }),
  adminProspectEnrichAll: (id) =>
    request(`/api/admin/prospects/${id}/enrich-all`, {
      method: "POST",
    }, { timeoutMs: 120000 }),
  adminProspectDelete: (id) =>
    request(`/api/admin/prospects/${id}`, { method: "DELETE" }),
  adminProspectArchive: (id) =>
    request(`/api/admin/prospects/${id}/archive`, { method: "POST" }),
  adminProspectsBulkAction: (payload) =>
    request("/api/admin/prospects/bulk-action", {
      method: "POST",
      body: JSON.stringify(payload),
    }, { timeoutMs: 300000, retries: 0 }),
  adminProspectionTechScan: (payload) =>
    request("/api/admin/prospection/tech-scan", {
      method: "POST",
      body: JSON.stringify(payload),
    }, { timeoutMs: 300000, retries: 0 }),
  adminProspectionCsvImport: (payload) =>
    request("/api/admin/prospection/csv-import", {
      method: "POST",
      body: JSON.stringify(payload),
    }, { timeoutMs: 120000 }),
  adminProspectionNewDomains: (payload) =>
    request("/api/admin/prospection/new-domains", {
      method: "POST",
      body: JSON.stringify(payload),
    }, { timeoutMs: 30000 }),
  adminProspectionDiscoverWebsites: (payload) =>
    request("/api/admin/prospection/discover-websites", {
      method: "POST",
      body: JSON.stringify(payload),
    }, { timeoutMs: 120000 }),
  adminProspectRules: () => request("/api/admin/prospects/rules"),
  adminProspectsExportCsvUrl: (query = "") =>
    `${API_BASE_URL}/api/admin/prospects/export.csv${query ? `?${query}` : ""}`,
  requestRaw,
};

export default apiClient;
